import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { ProductReviews } from '../../../common/model';
import { ButtonComponent } from '../../UI/ButtonComponent/ButtonComponent';
import { LABELS } from './model';
import './ReviewsAndRatings.scss';
import { averageRatingReviews, totalRatingReviews } from '../../../common/constants';
import { ScreenType } from '../../../common/view-model';
import { useSelector } from 'react-redux';
import { RootState } from '../../../redux/AppReducer';

interface ReviewsOverlayProps {
	isOpen: boolean;
	onClose: () => void;
	title?: string;
	children: React.ReactNode;
	averageRating: number;
}

export const ReviewsOverlay: React.FC<ReviewsOverlayProps> = ({
	isOpen,
	onClose,
	title = 'Dialog',
	children,
	averageRating
}) => {
	const dialogRef = useRef<HTMLDivElement | null>(null);
	const portalTarget = document.body;
	const { deviceScreen } = useSelector((state: RootState) => state.appState!);

	useEffect(() => {
		if (!isOpen) return;
		const prev = document.body.style.overflow;
		document.body.style.overflow = 'hidden';
		return () => {
			document.body.style.overflow = prev;
		};
	}, [isOpen]);

	useEffect(() => {
		if (isOpen && dialogRef.current) {
			const id = requestAnimationFrame(() => dialogRef.current?.focus());
			return () => cancelAnimationFrame(id);
		}
	}, [isOpen]);

	if (!isOpen) return null;

	const content = (
		<div className='overlay-backdrop'>
			<div
				className='overlay-dialog'
				role='dialog'
				aria-modal='true'
				aria-label={title}
				tabIndex={-1}
				ref={dialogRef}
			>
				<div className='overlay-header'>
					<h2 className='overlay-title'>{LABELS.heading}</h2>
					{deviceScreen == ScreenType.MOBILE && (
						<button type='button' className='overlay-close' onClick={onClose} aria-label='Close'>
							×
						</button>
					)}
				</div>

				<div className='overlay-subheader'>
					<span className='text'>{LABELS.subHeading}</span>
					{deviceScreen != ScreenType.MOBILE && (
						<button type='button' className='overlay-close' onClick={onClose} aria-label='Close'>
							×
						</button>
					)}
				</div>
				<div className='average-rating' aria-label={`Average rating ${averageRating} out of 5`}>
					<i className='ri-star-fill icon' />
					<span className='label'>
						{' '}
						{averageRating} {LABELS.starRating}
					</span>
				</div>
				<div className='overlay-content'>{children}</div>
			</div>
		</div>
	);
	return ReactDOM.createPortal(content, portalTarget);
};

interface ReviewsAndRatingsProps {
	reviews?: (ProductReviews | null)[] | null;
}

function addSpace(name: string): string {
	return name.replace(/([a-z])([A-Z])/g, '$1 $2');
}

function formatName(name: string): string {
	return name
		.split(' ')
		.map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
		.join(' ');
}

const toNumber = (val: unknown, fallback = 0): number => {
	const n = typeof val === 'string' ? Number(val) : typeof val === 'number' ? val : NaN;
	return Number.isFinite(n) ? n : fallback;
};

export const ReviewsAndRatingsComponent: React.FC<ReviewsAndRatingsProps> = ({ reviews }) => {
	const normalized: ProductReviews[] = useMemo(() => {
		const raw = reviews ?? [];
		return raw.filter(Boolean) as ProductReviews[];
	}, [reviews]);

	totalRatingReviews.value = normalized.length;

	const [expandedMain, setExpandedMain] = useState<{ [key: string]: boolean }>({});
	const [expandedOverlay, setExpandedOverlay] = useState<{ [key: string]: boolean }>({});

	const mainRefs = useRef<{ [key: string]: HTMLParagraphElement | null }>({});
	const overlayRefs = useRef<{ [key: string]: HTMLParagraphElement | null }>({});

	const [isOverflowingMain, setIsOverflowingMain] = useState<{ [key: string]: boolean }>({});
	const [isOverflowingOverlay, setIsOverflowingOverlay] = useState<{ [key: string]: boolean }>({});

	const toggleExpandMain = useCallback((key: string) => {
		setExpandedMain(prev => ({ ...prev, [key]: !prev[key] }));
	}, []);

	const toggleExpandOverlay = useCallback((key: string) => {
		setExpandedOverlay(prev => ({ ...prev, [key]: !prev[key] }));
	}, []);

	const averageRating = useMemo(() => {
		const nums = normalized.map(r => toNumber(r.starRatings, NaN)).filter(Number.isFinite);
		if (!nums.length) return 0;
		const sum = nums.reduce((acc, n) => acc + n, 0);
		return Math.round((sum / nums.length) * 10) / 10;
	}, [normalized]);

	averageRatingReviews.value = averageRating;

	const displayedReviews = normalized.slice(0, 4);

	const [overlayOpen, setOverlayOpen] = useState(false);

	useLayoutEffect(() => {
		let rafId: number | null = null;

		const measure = () => {
			const next: Record<string, boolean> = {};
			displayedReviews.forEach((review, index) => {
				const key = review.reviewId ?? `review-${index}`;
				const el = mainRefs.current[key];
				if (el) {
					next[key] = el.scrollHeight > el.clientHeight;
				}
			});
			setIsOverflowingMain(next);
		};

		rafId = requestAnimationFrame(measure);

		return () => {
			if (rafId !== null) cancelAnimationFrame(rafId);
		};
	}, [displayedReviews]);

	useEffect(() => {
		const observers: ResizeObserver[] = [];

		const remeasure = () => {
			const next: Record<string, boolean> = {};
			displayedReviews.forEach((review, index) => {
				const key = review.reviewId ?? `review-${index}`;
				const el = mainRefs.current[key];
				if (el) next[key] = el.scrollHeight > el.clientHeight;
			});
			setIsOverflowingMain(next);
		};

		displayedReviews.forEach((review, index) => {
			const key = review.reviewId ?? `review-${index}`;
			const el = mainRefs.current[key];
			if (!el) return;

			const ro = new ResizeObserver(() => {
				requestAnimationFrame(remeasure);
			});
			ro.observe(el);
			observers.push(ro);
		});

		return () => {
			observers.forEach(ro => ro.disconnect());
		};
	}, [displayedReviews]);

	useLayoutEffect(() => {
		if (!overlayOpen) return;
		let rafId: number | null = null;

		const measure = () => {
			const next: Record<string, boolean> = {};
			normalized.forEach((r, idx) => {
				const key = String(r.reviewId ?? `all-review-${idx}`);
				const el = overlayRefs.current[key];
				if (el) next[key] = el.scrollHeight > el.clientHeight;
			});
			setIsOverflowingOverlay(next);
		};

		rafId = requestAnimationFrame(measure);
		return () => {
			if (rafId !== null) cancelAnimationFrame(rafId);
		};
	}, [overlayOpen, normalized]);

	useEffect(() => {
		if (!overlayOpen) return;
		const observers: ResizeObserver[] = [];

		const remeasure = () => {
			const next: Record<string, boolean> = {};
			normalized.forEach((r, idx) => {
				const key = String(r.reviewId ?? `all-review-${idx}`);
				const el = overlayRefs.current[key];
				if (el) next[key] = el.scrollHeight > el.clientHeight;
			});
			setIsOverflowingOverlay(next);
		};

		normalized.forEach((r, idx) => {
			const key = String(r.reviewId ?? `all-review-${idx}`);
			const el = overlayRefs.current[key];
			if (!el) return;

			const ro = new ResizeObserver(() => {
				requestAnimationFrame(remeasure);
			});
			ro.observe(el);
			observers.push(ro);
		});

		return () => {
			observers.forEach(ro => ro.disconnect());
		};
	}, [overlayOpen, normalized]);

	return (
		<section className='reviews-and-ratings-container'>
			<header className='reviews-header'>
				<h4 className='heading'>{LABELS.heading}</h4>
				<h3 className='subheading'>{LABELS.subHeading}</h3>
			</header>

			<div className='average-rating' aria-label={`Average rating ${averageRating} out of 5`}>
				<i className='ri-star-fill icon' />
				<span className='label'>
					{' '}
					{averageRating} {LABELS.starRating}
				</span>
			</div>

			<ul className='reviews'>
				{displayedReviews.map((review, index) => {
					const key = review.reviewId ?? `review-${index}`;
					const isExpanded = expandedMain[key] ?? false;
					const ratingNumber = toNumber(review.starRatings, 0);
					const userName = review.userNickname ?? '';
					const titleText = review.reviewTitle ?? '';
					const commentText = review.reviewText ?? '';
					const reviewedDate = review.reviewSubmissionTime
						? new Date(review.reviewSubmissionTime).toLocaleDateString('en-US', {
								month: 'long',
								day: 'numeric',
								year: 'numeric'
						  })
						: '';

					return (
						<li key={key} className='review-card'>
							<header className='header'>
								<svg
									xmlns='http://www.w3.org/2000/svg'
									viewBox='0 0 512 512'
									width='32'
									height='32'
									aria-hidden='true'
								>
									<circle cx='256' cy='256' r='256' fill='rgba(153, 219, 235, 1)' />
									<circle cx='256' cy='192' r='64' stroke='#fff' strokeWidth='30' fill='none' />
									<path
										d='M128 384c32-48 96-64 128-64s96 16 128 64'
										stroke='#fff'
										strokeWidth='30'
										fill='none'
										strokeLinecap='round'
									/>
								</svg>

								<h3 className='user'>{formatName(addSpace(userName))}.</h3>
								<span className='dot-separator'>&middot;</span>
								{reviewedDate && <h3 className='date'>Reviewed {reviewedDate}</h3>}

								<div className='verified-label'>
									<span className='text'>{LABELS.verifiedLabel}</span>
								</div>
							</header>

							<div className='title'>
								<span className='ratings'>
									{Array.from({ length: 5 }).map((_, i) => (
										<i
											key={i}
											className={i < ratingNumber ? 'ri-star-fill icon' : 'ri-star-line icon'}
										/>
									))}
								</span>
								<span className='text'>{titleText}</span>
							</div>

							<div className='user-review'>
								<p
									ref={el => {
										mainRefs.current[key] = el;
									}}
									className={`text ${isExpanded ? 'expanded' : ''}`}
								>
									{commentText}
								</p>
							</div>

							{(isOverflowingMain[key] || isExpanded) && (
								<div className='readmore-less-section'>
									<span className='readmore-less' onClick={() => toggleExpandMain(key)}>
										{isExpanded ? 'Read Less' : 'Read More'}
									</span>
								</div>
							)}
						</li>
					);
				})}
			</ul>

			{normalized?.length > 4 && (
				<div className='load-more-reviews'>
					<ButtonComponent
						onClick={() => {
							setOverlayOpen(true);
						}}
						style='rounded-button secondary'
						text={LABELS.viewAllProducts}
					/>
				</div>
			)}

			<ReviewsOverlay
				isOpen={overlayOpen}
				onClose={() => setOverlayOpen(false)}
				title='All Reviews'
				averageRating={averageRating}
			>
				<ul className='reviews-list'>
					{}
					{normalized.map((r, idx) => {
						const rating =
							typeof r.starRatings === 'number' ? r.starRatings : Number(r.starRatings ?? 0) || 0;

						const key = String(r.reviewId ?? `all-review-${idx}`);
						const userName = r.userNickname ?? '';
						const titleText = r.reviewTitle ?? '';
						const commentText = r.reviewText ?? '';
						const reviewedDate = r.reviewSubmissionTime
							? new Date(r.reviewSubmissionTime).toLocaleDateString('en-US', {
									month: 'long',
									day: 'numeric',
									year: 'numeric'
							  })
							: '';
						const isExpanded = expandedOverlay[key] ?? false;

						return (
							<li key={key} className='reviews-list-item'>
								<div className='header'>
									<svg
										xmlns='http://www.w3.org/2000/svg'
										viewBox='0 0 512 512'
										width='32'
										height='32'
										aria-hidden='true'
									>
										<circle cx='256' cy='256' r='256' fill='rgba(153, 219, 235, 1)' />
										<circle cx='256' cy='192' r='64' stroke='#fff' strokeWidth='30' fill='none' />
										<path
											d='M128 384c32-48 96-64 128-64s96 16 128 64'
											stroke='#fff'
											strokeWidth='30'
											fill='none'
											strokeLinecap='round'
										/>
									</svg>
									{userName && <span className='user'>{formatName(addSpace(userName))}.</span>}
									{reviewedDate && (
										<>
											{userName && <span className='dot-separator'>•</span>}
											<time className='date'>Reviewed {reviewedDate}</time>
										</>
									)}

									<span className='verified-label'>{LABELS.verifiedLabel}</span>
								</div>

								<div className='title' aria-label={`Rating ${rating} out of 5`}>
									{Array.from({ length: 5 }).map((_, i) => (
										<i key={i} className={i < rating ? 'ri-star-fill icon' : 'ri-star-line icon'} />
									))}
									{titleText && <span className='text'>{titleText}</span>}
								</div>

								<p
									ref={el => {
										overlayRefs.current[key] = el;
									}}
									className={`user-review ${isExpanded ? 'expanded' : ''}`}
								>
									{commentText}
								</p>

								{(isOverflowingOverlay[key] || isExpanded) && (
									<div className='readmore-less-section'>
										<span className='readmore-less' onClick={() => toggleExpandOverlay(key)}>
											{isExpanded ? 'Read Less' : 'Read More'}
										</span>
									</div>
								)}
							</li>
						);
					})}
				</ul>
			</ReviewsOverlay>
		</section>
	);
};
